import type { Maybe } from '@quipex/core/primitives';

const WORD_PATTERN = /[\p{L}\p{M}\p{N}_]+(?:['’][\p{L}\p{M}\p{N}_]+)*/gu;

function capitaliseFirstCharacter(value: string): string {
    const [firstCharacter = '', ...remainingCharacters] = [...value];

    return firstCharacter.toUpperCase() + remainingCharacters.join('');
}

function normaliseWord(word: string): string {
    const normalised = capitaliseFirstCharacter(word.toLowerCase());

    return normalised.replace(
        /^(\p{L})(['’])(\p{L})/u,
        (_, prefix: string, apostrophe: string, following: string) =>
            `${prefix}${apostrophe}${following.toUpperCase()}`
    );
}

/**
 * Capitalises the first letter of each word in a string.
 *
 * @param value - The input string to transform.
 * @param preserveCase - If true, only capitalises each word's first character.
 * Defaults to false.
 * @returns The transformed string with each word capitalised correctly.
 */
export function toCapitalise(
    value: Maybe<string>,
    preserveCase = false
): string {
    if (!value) return '';

    return value.replace(WORD_PATTERN, (word) => {
        return preserveCase
            ? capitaliseFirstCharacter(word)
            : normaliseWord(word);
    });
}

/**
 * Converts a string to kebab-case.
 *
 * @param value - The input string to transform.
 * @returns The transformed string in kebab-case.
 */
export function toKebabCase(value: Maybe<string>): string {
    if (!value) return '';

    return value
        .trim() // remove leading and trailing whitespace
        .replace(/\s+/g, '-') // replace spaces with hyphens
        .replace(/([a-z])([A-Z])/g, '$1-$2') // replace camelCase with kebab-case
        .toLowerCase();
}

/**
 * Converts a string to sentence case, capitalising only the first letter of the first word.
 *
 * @param value - The input string to transform.
 * @param preserveCase - If true, only capitalises first letter without lowercasing the rest. Defaults to false.
 * @returns The transformed string in sentence case.
 */
export function toSentenceCase(
    value: Maybe<string>,
    preserveCase = false
): string {
    if (!value) return '';

    const trimmedValue = value.trim();
    if (preserveCase) {
        return trimmedValue.charAt(0).toUpperCase() + trimmedValue.slice(1);
    } else {
        const lowerCaseValue = trimmedValue.toLowerCase();
        return lowerCaseValue.charAt(0).toUpperCase() + lowerCaseValue.slice(1);
    }
}

/**
 * Converts a string to snake_case.
 *
 * @param value - The input string to transform.
 * @returns The transformed string in snake_case.
 */
export function toSnakeCase(value: Maybe<string>): string {
    if (!value) return '';

    return value
        .trim() // remove leading and trailing whitespace
        .replace(/\s+/g, '_') // replace spaces with underscores
        .replace(/([a-z])([A-Z])/g, '$1_$2') // replace camelCase with snake_case
        .toLowerCase();
}
